import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { restaurants } from '@/data/mocks/restaurants';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import {
  MessageSquare,
  Globe,
  Clock,
  TrendingUp,
  Users,
  Zap,
  CheckCircle2,
  ArrowRight,
  Brain,
  Languages,
  BarChart3,
  Target,
  Sparkles,
  Phone,
  Mail,
  Calendar,
  Star,
  Euro,
  MessageCircle,
  Bot,
  FileText
} from 'lucide-react';

export const ChatbotPage: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* En-tête */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center gap-3">
            <Bot className="w-7 h-7 sm:w-8 sm:h-8 text-stratos-gold" />
            Service Client IA 24/7
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mt-2">
            Chatbot multilingue intelligent pour répondre à vos visiteurs instantanément
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {Object.values(restaurants).map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.id ? "default" : "outline"}
              onClick={() => setSelectedRestaurant(restaurant.id)}
              className="flex items-center gap-2 justify-center"
            >
              <MessageSquare className="w-4 h-4" />
              {restaurant.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Pourquoi un Chatbot IA ? */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-2 border-stratos-gold bg-gradient-to-br from-blue-50 to-white">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-stratos-gold" />
              Pourquoi un Chatbot IA pour votre restaurant ?
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Ne perdez plus jamais une réservation à cause d'une question sans réponse
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg border-2 border-green-200 text-center">
                <Clock className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">24/7 Disponible</h3>
                <p className="text-sm text-gray-600">
                  Réponses instantanées même à 2h du matin ou les jours fériés
                </p>
              </div>

              <div className="p-4 bg-white rounded-lg border-2 border-blue-200 text-center">
                <Languages className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">Multilingue</h3>
                <p className="text-sm text-gray-600">
                  FR/EN/ES/PT pour capter la clientèle internationale
                </p>
              </div>

              <div className="p-4 bg-white rounded-lg border-2 border-purple-200 text-center">
                <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">+15-25% Conversions</h3>
                <p className="text-sm text-gray-600">
                  Amélioration progressive du taux de réservation
                </p>
              </div>

              <div className="p-4 bg-white rounded-lg border-2 border-orange-200 text-center">
                <Euro className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">ROI 300%+</h3>
                <p className="text-sm text-gray-600">
                  Retour sur investissement estimé
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Cas d'usage concrets */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-stratos-gold" />
              Questions Gérées par le Chatbot IA
            </CardTitle>
            <CardDescription>
              Exemples de conversations automatisées intelligentes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Scénario 1 */}
              <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Visiteur international</h4>
                    <p className="text-xs text-gray-500">En anglais, 23h45</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>👤 Client :</strong> "Do you have a table with Eiffel Tower view tomorrow at 8pm for 2 people?"
                    </p>
                  </div>
                  <div className="bg-stratos-gold/10 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>🤖 Chatbot :</strong> "Yes! We have availability for 2 at 8pm with Eiffel Tower view. Our romantic table is perfect for special occasions. Shall I send you the reservation link?"
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Réservation capturée en 30 secondes</span>
                  </div>
                </div>
              </div>

              {/* Scénario 2 */}
              <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <Target className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Demande de privatisation</h4>
                    <p className="text-xs text-gray-500">En français, 14h20</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>👤 Client :</strong> "Bonjour, combien coûte une privatisation pour 50 personnes ?"
                    </p>
                  </div>
                  <div className="bg-stratos-gold/10 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>🤖 Chatbot :</strong> "Excellente question ! Pour 50 personnes, nos formules privatisation commencent à partir de 5000€. Je vous envoie notre brochure détaillée et notre responsable événementiel vous contacte sous 2h ?"
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Lead qualifié capturé (valeur 50-200k€)</span>
                  </div>
                </div>
              </div>

              {/* Scénario 3 */}
              <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Calendar className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Info pratique</h4>
                    <p className="text-xs text-gray-500">En espagnol, 11h30</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>👤 Cliente :</strong> "¿A qué hora abren para la cena? ¿Tienen menú vegetariano?"
                    </p>
                  </div>
                  <div className="bg-stratos-gold/10 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>🤖 Chatbot :</strong> "¡Hola! Abrimos a las 19:00 para la cena. Sí, tenemos un menú degustación vegetariano completo. ¿Desea ver el menú?"
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Barrière langue supprimée</span>
                  </div>
                </div>
              </div>

              {/* Scénario 4 */}
              <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-yellow-100 rounded-full">
                    <Star className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Occasion spéciale</h4>
                    <p className="text-xs text-gray-500">En anglais, 16h00</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>👤 Client :</strong> "Planning to propose to my girlfriend. Do you help with special arrangements?"
                    </p>
                  </div>
                  <div className="bg-stratos-gold/10 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>🤖 Chatbot :</strong> "Congratulations! We'd be honored to help. We offer champagne, roses, photographer, and can customize the dessert. Our event manager will call you within 1 hour to plan everything."
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Expérience premium garantie</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Avantages Mesurables */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="border-2 border-green-500">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-green-600" />
              Impact Mesurable sur Vos Réservations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200">
                <TrendingUp className="w-10 h-10 text-green-600 mb-3" />
                <div className="text-3xl font-bold text-green-600 mb-1">+15-25%</div>
                <p className="text-sm text-gray-700 font-medium">Taux de conversion</p>
                <p className="text-xs text-gray-500 mt-1">Amélioration progressive du taux</p>
              </div>

              <div className="p-5 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200">
                <Users className="w-10 h-10 text-blue-600 mb-3" />
                <div className="text-3xl font-bold text-blue-600 mb-1">30-50</div>
                <p className="text-sm text-gray-700 font-medium">Réservations/mois</p>
                <p className="text-xs text-gray-500 mt-1">Estimées via le chatbot</p>
              </div>

              <div className="p-5 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-200">
                <Clock className="w-10 h-10 text-purple-600 mb-3" />
                <div className="text-3xl font-bold text-purple-600 mb-1">&lt;1min</div>
                <p className="text-sm text-gray-700 font-medium">Temps de réponse</p>
                <p className="text-xs text-gray-500 mt-1">Disponibilité 24/7</p>
              </div>

              <div className="p-5 bg-gradient-to-br from-orange-50 to-white rounded-xl border border-orange-200">
                <Euro className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-bold text-orange-600 mb-1">3-5k€</div>
                <p className="text-sm text-gray-700 font-medium">CA additionnel/mois</p>
                <p className="text-xs text-gray-500 mt-1">Potentiel estimé</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Fonctionnalités du Chatbot */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <Brain className="w-6 h-6 text-stratos-gold" />
              Intelligence Artificielle Entraînée sur Votre Restaurant
            </CardTitle>
            <CardDescription>
              Le chatbot connaît tous les détails de votre établissement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Colonne 1 */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-stratos-gold" />
                  Informations Maîtrisées
                </h4>
                <div className="space-y-3 pl-7">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Horaires d'ouverture</p>
                      <p className="text-xs text-gray-600">Heures de service, jours fériés, fermetures exceptionnelles</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Menu complet</p>
                      <p className="text-xs text-gray-600">Plats, prix, allergènes, options végétariennes/vegan</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Contact & Réservation</p>
                      <p className="text-xs text-gray-600">Téléphone, email, lien booking, politique d'annulation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Globe className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Localisation & Accès</p>
                      <p className="text-xs text-gray-600">Adresse, métro, parking, accessibilité PMR</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne 2 */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-stratos-gold" />
                  Capacités Avancées
                </h4>
                <div className="space-y-3 pl-7">
                  <div className="flex items-start gap-3">
                    <Target className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Privatisation & Événements</p>
                      <p className="text-xs text-gray-600">Capacité, tarifs, options, équipements (projecteur, sono...)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Star className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Occasions spéciales</p>
                      <p className="text-xs text-gray-600">Anniversaire, demande en mariage, saint-valentin, arrangements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Qualification de leads</p>
                      <p className="text-xs text-gray-600">Collecte email/téléphone, transmission à l'équipe</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Languages className="w-4 h-4 text-stratos-gold mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Détection automatique de langue</p>
                      <p className="text-xs text-gray-600">S'adapte automatiquement à la langue du visiteur</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Processus de mise en place */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <Zap className="w-6 h-6 text-stratos-gold" />
              Mise en Place Simple & Rapide
            </CardTitle>
            <CardDescription>
              Chatbot opérationnel en 7-10 jours
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                {
                  step: 1,
                  title: 'Collecte d\'informations',
                  description: 'Nous récupérons : menu, horaires, politique de réservation, FAQ existante, infos privatisation',
                  duration: '2-3 jours'
                },
                {
                  step: 2,
                  title: 'Entraînement IA',
                  description: 'Notre équipe nourrit l\'IA avec vos données et simule 100+ conversations pour garantir la qualité',
                  duration: '3-4 jours'
                },
                {
                  step: 3,
                  title: 'Intégration technique',
                  description: 'Installation du widget sur votre site (1 ligne de code), responsive design, branding aux couleurs du restaurant',
                  duration: '1 jour'
                },
                {
                  step: 4,
                  title: 'Tests & Ajustements',
                  description: 'Vous testez, nous ajustons les réponses selon vos retours',
                  duration: '1-2 jours'
                }
              ].map((step) => (
                <div key={step.step} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-stratos-gold flex items-center justify-center text-gray-900 font-bold">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{step.title}</h4>
                      <Badge variant="outline" className="text-xs">
                        {step.duration}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Timeline total */}
            <div className="mt-6 p-4 bg-stratos-gold/10 rounded-lg border-2 border-stratos-gold text-center">
              <p className="text-sm text-gray-700 mb-2">Délai total de mise en service</p>
              <p className="text-3xl font-bold text-stratos-gold">7-10 jours</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Inclus dans les packs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="bg-gradient-to-br from-stratos-gold/5 to-white border-2 border-stratos-gold/30">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-stratos-gold" />
              Inclus dans les Packs Premium & Signature
            </CardTitle>
            <CardDescription>
              Le chatbot IA est disponible à partir du Pack Premium
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pack Premium */}
              <div className="p-6 bg-gradient-to-br from-stratos-gold/20 to-white rounded-xl border-2 border-stratos-gold">
                <Badge className="bg-stratos-gold text-gray-900 mb-3">⭐ Recommandé</Badge>
                <h3 className="font-bold text-lg mb-4 text-gray-900">Pack Premium</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Chatbot FR + EN + ES</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">FAQ enrichie (50+ questions)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Privatisation & événements</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Qualification leads avancée</span>
                  </div>
                </div>
              </div>

              {/* Pack Signature */}
              <div className="p-6 bg-white rounded-xl border-2 border-gray-200">
                <h3 className="font-bold text-lg mb-4 text-gray-900">Pack Signature</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Chatbot 4 langues (FR/EN/ES/PT)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">FAQ illimitée personnalisée</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Analytics conversationnel</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Intégration CRM/calendrier</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Formation équipe</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Statistiques & Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-stratos-gold" />
              Dashboard Analytics du Chatbot
            </CardTitle>
            <CardDescription>
              Suivez les performances en temps réel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                <p className="text-2xl font-bold text-blue-600">2 847</p>
                <p className="text-xs text-gray-600 mt-1">Conversations ce mois</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
                <p className="text-2xl font-bold text-green-600">42%</p>
                <p className="text-xs text-gray-600 mt-1">Taux de conversion</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center">
                <p className="text-2xl font-bold text-purple-600">18s</p>
                <p className="text-xs text-gray-600 mt-1">Temps de réponse moyen</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 text-center">
                <p className="text-2xl font-bold text-orange-600">87%</p>
                <p className="text-xs text-gray-600 mt-1">Satisfaction client</p>
              </div>
            </div>

            {/* Questions les plus fréquentes */}
            <div className="mt-6">
              <h4 className="font-semibold mb-4">Top 5 Questions Posées</h4>
              <div className="space-y-2">
                {[
                  { question: 'Horaires d\'ouverture ?', count: 342 },
                  { question: 'Prix du menu dégustation ?', count: 298 },
                  { question: 'Réserver pour ce soir ?', count: 265 },
                  { question: 'Options végétariennes ?', count: 187 },
                  { question: 'Privatisation pour 40 personnes ?', count: 156 }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">{item.question}</span>
                    <Badge variant="outline">{item.count} fois</Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* CTA Final */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <Card className="border-2 border-stratos-gold bg-gradient-to-br from-stratos-gold/10 to-white">
          <CardContent className="pt-6 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Ne Perdez Plus Jamais une Réservation
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Le chatbot IA STRATOS répond aux visiteurs 24/7 en 4 langues, 
              améliore l'expérience client et facilite la prise de réservation.
            </p>
            <Link to="/proposal">
              <Button
                size="lg"
                className="bg-gradient-to-r from-stratos-gold to-stratos-gold-dark text-gray-900 hover:shadow-lg hover:shadow-stratos-gold/30"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Découvrir les Packs avec Chatbot IA
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

